import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, Row, Table } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const DEFAULT_SECTIONS = [
 "Reports & Drawings",
 "Planning & Control",
 "Safety & Docs",
 "Execution",
 "Quality & Closure",
 "Commercial",
];

const empty = { label: "", section: DEFAULT_SECTIONS[0], navigateTo: "", permission: "", isHighlight: false, order: 0 };

const ProjectMenuButtonMaster = () => {
 const [buttons, setButtons] = useState([]);
 const [sections, setSections] = useState(DEFAULT_SECTIONS);
 const [loading, setLoading] = useState(true);
 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [form, setForm] = useState(empty);
 const [saving, setSaving] = useState(false);
 const [isNewSection, setIsNewSection] = useState(false);
 const [newSectionName, setNewSectionName] = useState("");
 const [editingSection, setEditingSection] = useState(null); // section name being renamed
 const [editingSectionValue, setEditingSectionValue] = useState("");

 useEffect(() => {
 fetchButtons();
 }, []);

 const fetchButtons = async () => {
 setLoading(true);
 try {
 const res = await api.get("/project-menu-buttons/all");
 setButtons(res.data.data || []);
 if (res.data.sections?.length) setSections(res.data.sections);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to load", "error");
 } finally {
 setLoading(false);
 }
 };

 const openAdd = () => {
 setEditId(null);
 setIsNewSection(false);
 setNewSectionName("");
 setForm({ ...empty, section: sections[0] || DEFAULT_SECTIONS[0] });
 setShowModal(true);
 };

 const openEdit = (btn) => {
 setEditId(btn._id);
 setIsNewSection(false);
 setNewSectionName("");
 setForm({
 label: btn.label,
 section: btn.section,
 navigateTo: btn.navigateTo,
 permission: btn.permission || "",
 isHighlight: !!btn.isHighlight,
 isActive: btn.isActive !== false,
 order: btn.order || 0,
 });
 setShowModal(true);
 };

 const handleSave = async (e) => {
 e.preventDefault();

 // Resolve section name
 const resolvedSection = isNewSection ? newSectionName.trim() : form.section;
 if (!resolvedSection) {
 swal("Error", "Section name cannot be empty", "error");
 return;
 }

 const payload = { ...form, section: resolvedSection };

 setSaving(true);
 try {
 if (editId) {
 const res = await api.patch(`/project-menu-buttons/${editId}`, payload);
 setButtons((prev) => prev.map((b) => (b._id === editId ? res.data.data : b)));
 // Add new section to local list if not present
 if (!sections.includes(resolvedSection)) setSections(prev => [...prev, resolvedSection]);
 swal("Updated", "Button updated successfully", "success");
 } else {
 const res = await api.post("/project-menu-buttons", payload);
 setButtons((prev) => [...prev, res.data.data]);
 if (!sections.includes(resolvedSection)) setSections(prev => [...prev, resolvedSection]);
 swal("Created", "Button added successfully", "success");
 }
 setShowModal(false);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save", "error");
 } finally {
 setSaving(false);
 }
 };

 const handleDelete = async (id) => {
 const ok = await swal({ title: "Delete button?", icon: "warning", buttons: true, dangerMode: true });
 if (!ok) return;
 try {
 await api.delete(`/project-menu-buttons/${id}`);
 setButtons((prev) => prev.filter((b) => b._id !== id));
 swal("Deleted", "Button removed", "success");
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to delete", "error");
 }
 };

 const toggleActive = async (btn) => {
 try {
 const res = await api.patch(`/project-menu-buttons/${btn._id}`, { isActive: !btn.isActive });
 setButtons((prev) => prev.map((b) => (b._id === btn._id ? res.data.data : b)));
 } catch (err) {
 swal("Error", "Failed to update", "error");
 }
 };

 const handleDeleteSection = async (section) => {
 // Default sections can only be empty — custom sections are completely removed
 const isDefault = DEFAULT_SECTIONS.includes(section);
 const confirmMsg = isDefault
 ? `Delete all buttons in "${section}"? The section header will remain.`
 : `Delete entire section "${section}" and all its buttons? This cannot be undone.`;

 const ok = await swal({ title: "Delete Section?", text: confirmMsg, icon: "warning", buttons: true, dangerMode: true });
 if (!ok) return;

 try {
 await api.delete(`/project-menu-buttons/section/${encodeURIComponent(section)}`);
 setButtons((prev) => prev.filter((b) => b.section !== section));
 // Remove custom section from local list
 if (!isDefault) setSections((prev) => prev.filter((s) => s !== section));
 swal("Done", isDefault ? "All buttons removed" : "Section deleted", "success");
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to delete section", "error");
 }
 };

 const handleRenameSection = async (oldSection) => {
 const newName = editingSectionValue.trim();
 if (!newName || newName === oldSection) {
 setEditingSection(null);
 return;
 }

 try {
 await api.patch(`/project-menu-buttons/section/${encodeURIComponent(oldSection)}/rename`, { newSection: newName });
 // Update buttons locally
 setButtons((prev) => prev.map((b) => b.section === oldSection ? { ...b, section: newName } : b));
 // Update sections list
 setSections((prev) => prev.map((s) => s === oldSection ? newName : s));
 setEditingSection(null);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to rename", "error");
 }
 };

 // Group by section for display
 const grouped = sections.reduce((acc, s) => {
 acc[s] = buttons.filter((b) => b.section === s);
 return acc;
 }, {});

 return (
 <>
 <PageTitle activeMenu="Project Menu Buttons" motherMenu="Masters" />

 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
 <Card.Title className="mb-0 text-primary fw-bold">Project Card — Custom Buttons</Card.Title>
 <Button variant="primary" size="sm" onClick={openAdd}>
 <i className="fa fa-plus me-2"></i>Add Button
 </Button>
 </Card.Header>
 <Card.Body className="p-0">
 {loading ? (
 <div className="text-center py-5 text-muted">Loading...</div>
 ) : (
 sections.map((section) => (
 <div key={section}>
 <div className="px-4 py-2 bg-light fw-bold border-bottom d-flex align-items-center justify-content-between" style={{ fontSize: 13 }}>
 {/* Section name — inline edit */}
 {editingSection === section ? (
 <div className="d-flex align-items-center gap-2 flex-grow-1 me-3">
 <input
 className="form-control form-control-sm bg-white"
 style={{ maxWidth: 260 }}
 value={editingSectionValue}
 onChange={(e) => setEditingSectionValue(e.target.value)}
 onKeyDown={(e) => {
 if (e.key === "Enter") handleRenameSection(section);
 if (e.key === "Escape") setEditingSection(null);
 }}
 autoFocus
 />
 <Button size="sm" variant="success" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => handleRenameSection(section)}>
 <i className="fa fa-check"></i>
 </Button>
 <Button size="sm" variant="outline-secondary" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => setEditingSection(null)}>
 <i className="fa fa-times"></i>
 </Button>
 </div>
 ) : (
 <span>
 {section}
 <Badge bg="secondary" className="ms-2">{grouped[section]?.length || 0}</Badge>
 {!DEFAULT_SECTIONS.includes(section) && (
 <Button
 size="sm"
 variant="link"
 className="text-muted p-0 ms-2"
 style={{ fontSize: 11 }}
 title="Rename section"
 onClick={() => { setEditingSection(section); setEditingSectionValue(section); }}
 >
 <i className="fa fa-edit"></i>
 </Button>
 )}
 </span>
 )}
 {!DEFAULT_SECTIONS.includes(section) && (
 <Button
 size="sm"
 variant="outline-danger"
 style={{ fontSize: 11, padding: "2px 8px", flexShrink: 0 }}
 onClick={() => handleDeleteSection(section)}
 title="Delete this section"
 >
 <i className="fa fa-trash me-1"></i>Delete Section
 </Button>
 )}
 </div>
 {grouped[section].length === 0 ? (
 <div className="px-4 py-3 text-muted" style={{ fontSize: 13 }}>No custom buttons</div>
 ) : (
 <Table hover responsive className="mb-0 align-middle">
 <thead className="bg-light">
 <tr>
 <th className="px-4 py-2" style={{ fontSize: 12 }}>#</th>
 <th style={{ fontSize: 12 }}>Label</th>
 <th style={{ fontSize: 12 }}>Navigate To</th>
 <th style={{ fontSize: 12 }}>Permission</th>
 <th style={{ fontSize: 12 }}>Highlight</th>
 <th style={{ fontSize: 12 }}>Order</th>
 <th style={{ fontSize: 12 }}>Active</th>
 <th style={{ fontSize: 12 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {grouped[section].map((btn, i) => (
 <tr key={btn._id}>
 <td className="px-4 text-muted">{i + 1}</td>
 <td className="fw-medium">{btn.label}</td>
 <td>
 <code style={{ fontSize: 12 }}>{btn.navigateTo}</code>
 </td>
 <td>
 {btn.permission ? (
 <Badge bg="light" className="text-info border" style={{ fontSize: 11 }}>
 {btn.permission}
 </Badge>
 ) : (
 <span className="text-muted" style={{ fontSize: 12 }}>—</span>
 )}
 </td>
 <td>
 {btn.isHighlight ? <Badge bg="warning" text="dark">Yes</Badge> : <span className="text-muted">—</span>}
 </td>
 <td>{btn.order}</td>
 <td>
 <Form.Check
 type="switch"
 checked={btn.isActive}
 onChange={() => toggleActive(btn)}
 title={btn.isActive ? "Active" : "Inactive"}
 />
 </td>
 <td>
 <div className="d-flex gap-2">
 <Button size="sm" variant="outline-primary" onClick={() => openEdit(btn)}>
 <i className="fa fa-edit"></i>
 </Button>
 <Button size="sm" variant="outline-danger" onClick={() => handleDelete(btn._id)}>
 <i className="fa fa-trash"></i>
 </Button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </div>
 ))
 )}
 </Card.Body>
 </Card>

 {/* ADD / EDIT MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop={saving ? "static" : true}>
 <Modal.Header closeButton={!saving}>
 <Modal.Title className="text-primary fw-bold">
 {editId ? "Edit Button" : "Add Custom Button"}
 </Modal.Title>
 </Modal.Header>
 <Form onSubmit={handleSave}>
 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label className="fw-medium">Label <span className="text-danger">*</span></Form.Label>
 <Form.Control
 value={form.label}
 onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
 placeholder="e.g. Site Photos"
 required
 className="bg-light border-0"
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label className="fw-medium">Section <span className="text-danger">*</span></Form.Label>
 <Form.Select
 value={isNewSection ? "__new__" : form.section}
 onChange={(e) => {
 if (e.target.value === "__new__") {
 setIsNewSection(true);
 setNewSectionName("");
 } else {
 setIsNewSection(false);
 setForm((f) => ({ ...f, section: e.target.value }));
 }
 }}
 className="bg-light border-0"
 >
 {sections.map((s) => (
 <option key={s} value={s}>{s}</option>
 ))}
 <option value="__new__"> New Section...</option>
 </Form.Select>
 {isNewSection && (
 <Form.Control
 className="mt-2 bg-light border-0"
 placeholder="Enter new section name"
 value={newSectionName}
 onChange={(e) => setNewSectionName(e.target.value)}
 required
 autoFocus
 />
 )}
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label className="fw-medium">Navigate To <span className="text-danger">*</span></Form.Label>
 <Form.Control
 value={form.navigateTo}
 onChange={(e) => setForm((f) => ({ ...f, navigateTo: e.target.value }))}
 placeholder="e.g. /documents?workOrder={workOrderId}&documentType=PHOTOS"
 required
 className="bg-light border-0"
 />
 <Form.Text className="text-muted">
 Use <code>{"{projectId}"}</code> and <code>{"{workOrderId}"}</code> as dynamic placeholders.
 </Form.Text>
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label className="fw-medium">Permission <span className="text-muted fw-normal">(optional)</span></Form.Label>
 <Form.Control
 value={form.permission}
 onChange={(e) => setForm((f) => ({ ...f, permission: e.target.value }))}
 placeholder="e.g. read:documents"
 className="bg-light border-0"
 />
 <Form.Text className="text-muted">Leave blank to show to all users.</Form.Text>
 </Form.Group>

 <Row>
 <Col xs={6}>
 <Form.Group className="mb-3">
 <Form.Label className="fw-medium">Order</Form.Label>
 <Form.Control
 type="number"
 value={form.order}
 onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))}
 className="bg-light border-0"
 min={0}
 />
 </Form.Group>
 </Col>
 <Col xs={3} className="d-flex align-items-center pt-3">
 <Form.Check
 type="checkbox"
 label="Highlight"
 checked={form.isHighlight}
 onChange={(e) => setForm((f) => ({ ...f, isHighlight: e.target.checked }))}
 />
 </Col>
 {editId && (
 <Col xs={3} className="d-flex align-items-center pt-3">
 <Form.Check
 type="checkbox"
 label="Active"
 checked={form.isActive}
 onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
 />
 </Col>
 )}
 </Row>
 </Modal.Body>
 <Modal.Footer className="border-0 pt-0">
 <Button variant="light" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
 <Button variant="primary" type="submit" disabled={saving}>
 {saving ? "Saving..." : editId ? "Update" : "Add Button"}
 </Button>
 </Modal.Footer>
 </Form>
 </Modal>
 </>
 );
};

export default ProjectMenuButtonMaster;
